import { mapFallbackMarkup, loadMapboxGl, mapboxToken } from "../mapbox-client.js";
import {
  ARIZONA_FIT_PADDING,
  ARIZONA_MAP_BOUNDS,
  HEX_CAMERA_DESKTOP_BREAKPOINT,
  HEX_DESKTOP_MAX_BOUNDS,
  HEX_DESKTOP_MIN_ZOOM,
  HEX_NARROW_MAX_BOUNDS,
  INITIAL_MAP_CENTER,
  INITIAL_MAP_ZOOM,
} from "./config.js";
import { DEFAULT_HEX_DISPLAY_OPTIONS, hexLayerPresentation } from "./display-options.js";
import { getOverlayClusterExpansionZoom } from "./overlay-clustering.js";
import { DEFAULT_HEX_OVERLAY_VISIBILITY, HEX_OVERLAY_CATEGORIES, HEX_OVERLAY_STYLE, hexOverlayVisibility } from "./overlay-config.js";
import { overlayPopupMarkup } from "./overlay-popups.js";
import { formatCommunityReportCopy } from "../../community-report-copy.js";
import { createHexOverlayRenderer } from "./overlay-renderer.js";
import {
  DEFAULT_HEX_QUANTITY_THRESHOLDS,
  quantityDotRadiusExpression,
  quantityFillColorExpression,
} from "./quantity-styling.js";
import { createHexResolutionRenderer } from "./resolution-renderer.js";
import { HEX_BOUNDARY_STYLE, HEX_DOT_ONLY_BOUNDARY_STYLE, HEX_QUANTITY_FILL_STYLE, HEX_REPORT_COUNT_DOT_STYLE } from "./style-config.js";
import { normalizeHexSymptomSelection } from "./symptoms.js";
import { timeSummaryForHexReports } from "./time-series.js";

const POLYGON_SOURCE_ID = "hex-poc-polygons";
const CENTER_SOURCE_ID = "hex-poc-centers";
const FILL_LAYER_ID = "hex-poc-fills";
const BOUNDARY_LAYER_ID = "hex-poc-boundaries";
const DOT_LAYER_ID = "hex-poc-report-count-dots";

const NARROW_CAMERA_POLICY = Object.freeze({
  mode: "narrow",
  maxBounds: HEX_NARROW_MAX_BOUNDS,
  minZoom: null,
});
const DESKTOP_CAMERA_POLICY = Object.freeze({
  mode: "desktop",
  maxBounds: HEX_DESKTOP_MAX_BOUNDS,
  minZoom: HEX_DESKTOP_MIN_ZOOM,
});

export function hexCameraPolicyForWidth(containerWidth) {
  const width = Number(containerWidth);
  return Number.isFinite(width) && width >= HEX_CAMERA_DESKTOP_BREAKPOINT
    ? DESKTOP_CAMERA_POLICY
    : NARROW_CAMERA_POLICY;
}

function renderedMapContainerWidth(element) {
  return element.getBoundingClientRect().width || element.clientWidth;
}

export function reapplyHexCameraPolicyForWidth(map, containerWidth, currentMode) {
  const policy = hexCameraPolicyForWidth(containerWidth);
  if (policy.mode === currentMode) return policy.mode;

  map.setMaxBounds(policy.maxBounds);
  map.setMinZoom(policy.minZoom);
  map.fitBounds(ARIZONA_MAP_BOUNDS, { padding: ARIZONA_FIT_PADDING, duration: 0 });
  return policy.mode;
}

export async function initializeHexPocMap({
  elementId,
  reports,
  onResolutionChange = () => {},
  onZoomChange = () => {},
  onDisplayOptionsReady = () => {},
  getTimeState = () => null,
  getSelectedSymptoms = () => [],
  getLanguage = () => "EN",
  mapFallbackCopy = (key) => key,
  onTimeRendererReady = () => {},
  getQuantityThresholds = () => DEFAULT_HEX_QUANTITY_THRESHOLDS,
  onQuantityThresholdsReady = () => {},
  overlayReports = { animals: [], environment: [] },
  getOverlayVisibility = () => DEFAULT_HEX_OVERLAY_VISIBILITY,
  onOverlayVisibilityReady = () => {},
}) {
  const element = document.getElementById(elementId);
  if (!element) return null;
  const token = mapboxToken();
  if (!token) {
    element.innerHTML = mapFallbackMarkup(mapFallbackCopy("hex_mapbox_token"), mapFallbackCopy("hex_mapbox_help"));
    return null;
  }
  element.innerHTML = `<div class="map-fallback"><strong>${mapFallbackCopy("hex_loading")}</strong></div>`;
  await loadMapboxGl();
  element.innerHTML = "";
  window.mapboxgl.accessToken = token;
  const initialContainerWidth = renderedMapContainerWidth(element);
  const initialCameraPolicy = hexCameraPolicyForWidth(initialContainerWidth);
  const map = new window.mapboxgl.Map({
    container: elementId,
    style: "mapbox://styles/mapbox/light-v11",
    projection: "mercator",
    center: INITIAL_MAP_CENTER,
    zoom: INITIAL_MAP_ZOOM,
    maxBounds: initialCameraPolicy.maxBounds,
    ...(initialCameraPolicy.minZoom === null ? {} : { minZoom: initialCameraPolicy.minZoom }),
    attributionControl: false,
  });
  map.addControl(new window.mapboxgl.AttributionControl({ compact: true }), "bottom-right");
  let cameraPolicyMode = initialCameraPolicy.mode;
  map.on("resize", () => {
    cameraPolicyMode = reapplyHexCameraPolicyForWidth(
      map,
      renderedMapContainerWidth(element),
      cameraPolicyMode,
    );
  });

  let activePopup = null;
  let activePopupCategory = null;

  const removeActivePopup = (category = null) => {
    if (category && activePopupCategory !== category) return false;
    activePopup?.remove();
    activePopup = null;
    activePopupCategory = null;
    return true;
  };
  const setActivePopup = (popup, category) => {
    removeActivePopup();
    if (!popup) return;
    activePopup = popup;
    activePopupCategory = category;
    popup.on?.("close", () => {
      if (activePopup === popup) {
        activePopup = null;
        activePopupCategory = null;
      }
    });
  };

  // `style.load` is the readiness boundary for custom sources/layers. Unlike the
  // broader map `load` event, it does not wait for every base-map tile to settle.
  map.once("style.load", () => {
    map.fitBounds(ARIZONA_MAP_BOUNDS, { padding: ARIZONA_FIT_PADDING, duration: 0 });
    addHexSourcesAndLayers(map, getQuantityThresholds());
    addOverlaySourcesAndLayers(map);
    const renderer = createHexResolutionRenderer({
      reports,
      removeActivePopup,
      setFeatureCollections: (collections, resolution) => {
        map.getSource(POLYGON_SOURCE_ID).setData(collections.polygons);
        map.getSource(CENTER_SOURCE_ID).setData(collections.centers);
        applyHexStyleForResolution(map, resolution);
      },
      onResolutionChange,
    });
    const overlayRenderer = createHexOverlayRenderer({
      reportsByCategory: overlayReports,
      removeActivePopup,
      setFeatureCollections: (collections) => {
        Object.entries(HEX_OVERLAY_CATEGORIES).forEach(([category, config]) => {
          map.getSource(config.sourceId).setData(collections[category]);
        });
      },
    });
    const updateForCurrentState = () => {
      const sharedState = { zoom: map.getZoom(), timeState: getTimeState() };
      const hexUpdated = renderer.update({ ...sharedState, selectedSymptoms: getSelectedSymptoms() });
      const overlaysUpdated = overlayRenderer.update(sharedState);
      return hexUpdated || overlaysUpdated;
    };
    const updateForZoom = () => updateForCurrentState();
    updateForZoom();
    const setDisplayOptions = (options) => applyDisplayOptions(map, options);
    setDisplayOptions(DEFAULT_HEX_DISPLAY_OPTIONS);
    onDisplayOptionsReady(setDisplayOptions);
    onQuantityThresholdsReady((thresholds) => applyQuantityThresholds(map, thresholds));
    const setOverlayVisibility = (visibility) => applyOverlayVisibility(map, visibility, removeActivePopup);
    setOverlayVisibility(getOverlayVisibility());
    onOverlayVisibilityReady(setOverlayVisibility);
    onTimeRendererReady(updateForCurrentState);
    onZoomChange(map.getZoom());
    map.on("zoom", () => onZoomChange(map.getZoom()));
    map.on("zoomend", updateForZoom);
    [FILL_LAYER_ID, DOT_LAYER_ID].forEach((layerId) => {
      addHexInteractivity(map, layerId, setActivePopup, getTimeState, getSelectedSymptoms, getLanguage);
    });
    Object.values(HEX_OVERLAY_CATEGORIES).forEach((config) => {
      addOverlayInteractivity(map, config, overlayRenderer, setActivePopup, removeActivePopup, getLanguage);
    });
  });
  return map;
}

function addOverlaySourcesAndLayers(map) {
  Object.values(HEX_OVERLAY_CATEGORIES).forEach((config) => {
    map.addSource(config.sourceId, { type: "geojson", data: emptyFeatureCollection() });
    map.addLayer({
      id: config.pointLayerId,
      type: "circle",
      source: config.sourceId,
      filter: ["==", ["get", "kind"], config.pointKind],
      paint: {
        "circle-radius": HEX_OVERLAY_STYLE.pointRadius,
        "circle-color": config.color,
        "circle-stroke-color": HEX_OVERLAY_STYLE.pointStrokeColor,
        "circle-stroke-width": HEX_OVERLAY_STYLE.pointStrokeWidth,
      },
    });
    map.addLayer({
      id: config.clusterLayerId,
      type: "circle",
      source: config.sourceId,
      filter: ["==", ["get", "kind"], config.clusterKind],
      paint: {
        "circle-radius": ["get", "radius"],
        "circle-color": config.color,
        "circle-opacity": HEX_OVERLAY_STYLE.clusterOpacity,
        "circle-stroke-color": HEX_OVERLAY_STYLE.clusterStrokeColor,
        "circle-stroke-width": HEX_OVERLAY_STYLE.clusterStrokeWidth,
      },
    });
    map.addLayer({
      id: config.clusterCountLayerId,
      type: "symbol",
      source: config.sourceId,
      filter: ["==", ["get", "kind"], config.clusterKind],
      layout: {
        "text-field": ["get", "clusterLabel"],
        "text-size": HEX_OVERLAY_STYLE.clusterCountSize,
        "text-allow-overlap": true,
      },
      paint: { "text-color": HEX_OVERLAY_STYLE.clusterCountColor },
    });
  });
}

function addHexSourcesAndLayers(map, quantityThresholds) {
  map.addSource(POLYGON_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection() });
  map.addSource(CENTER_SOURCE_ID, { type: "geojson", data: emptyFeatureCollection() });
  map.addLayer({
    id: FILL_LAYER_ID,
    type: "fill",
    source: POLYGON_SOURCE_ID,
    paint: {
      "fill-color": quantityFillColorExpression(quantityThresholds),
      "fill-opacity": HEX_QUANTITY_FILL_STYLE.opacity,
    },
  });
  map.addLayer({
    id: BOUNDARY_LAYER_ID,
    type: "line",
    source: POLYGON_SOURCE_ID,
    paint: {
      "line-color": HEX_BOUNDARY_STYLE.color,
      "line-opacity": HEX_BOUNDARY_STYLE.opacity,
      "line-width": HEX_BOUNDARY_STYLE.width,
    },
  });
  map.addLayer({
    id: DOT_LAYER_ID,
    type: "circle",
    source: CENTER_SOURCE_ID,
    paint: {
      "circle-radius": quantityDotRadiusExpression(3),
      "circle-color": HEX_REPORT_COUNT_DOT_STYLE.color,
      "circle-opacity": HEX_REPORT_COUNT_DOT_STYLE.opacity,
      "circle-stroke-color": HEX_REPORT_COUNT_DOT_STYLE.strokeColor,
      "circle-stroke-width": HEX_REPORT_COUNT_DOT_STYLE.strokeWidth,
    },
  });
}

function applyHexStyleForResolution(map, resolution) {
  map.setPaintProperty(DOT_LAYER_ID, "circle-radius", quantityDotRadiusExpression(resolution));
}

export function applyQuantityThresholds(map, thresholds) {
  map.setPaintProperty(FILL_LAYER_ID, "fill-color", quantityFillColorExpression(thresholds));
}

function applyDisplayOptions(map, options) {
  const presentation = hexLayerPresentation(options);
  const boundaryStyle = presentation.useBlackBoundary ? HEX_DOT_ONLY_BOUNDARY_STYLE : HEX_BOUNDARY_STYLE;
  map.setLayoutProperty(FILL_LAYER_ID, "visibility", presentation.showShading ? "visible" : "none");
  map.setLayoutProperty(DOT_LAYER_ID, "visibility", presentation.showDots ? "visible" : "none");
  map.setPaintProperty(BOUNDARY_LAYER_ID, "line-color", boundaryStyle.color);
  map.setPaintProperty(BOUNDARY_LAYER_ID, "line-opacity", boundaryStyle.opacity);
  map.setPaintProperty(BOUNDARY_LAYER_ID, "line-width", boundaryStyle.width);
}

function applyOverlayVisibility(map, options, removeActivePopup) {
  const visibility = hexOverlayVisibility(options);
  Object.entries(HEX_OVERLAY_CATEGORIES).forEach(([category, config]) => {
    const layerVisibility = visibility[category] ? "visible" : "none";
    [config.pointLayerId, config.clusterLayerId, config.clusterCountLayerId].forEach((layerId) => {
      map.setLayoutProperty(layerId, "visibility", layerVisibility);
    });
    if (!visibility[category]) removeActivePopup(category);
  });
}

function addHexInteractivity(map, layerId, setActivePopup, getTimeState, getSelectedSymptoms, getLanguage) {
  map.on("click", layerId, (event) => {
    setActivePopup(showHexPopup(event, getTimeState(), getSelectedSymptoms(), getLanguage()), "human");
  });
  map.on("mouseenter", layerId, () => { map.getCanvas().style.cursor = "pointer"; });
  map.on("mouseleave", layerId, () => { map.getCanvas().style.cursor = ""; });
}

function addOverlayInteractivity(map, config, overlayRenderer, setActivePopup, removeActivePopup, getLanguage) {
  map.on("click", config.clusterLayerId, (event) => {
    const feature = event.features?.[0];
    if (!feature) return;
    const categoryIndex = overlayRenderer.getIndex(config.category);
    if (!categoryIndex) return;
    const expansionZoom = getOverlayClusterExpansionZoom(categoryIndex, feature.properties?.clusterId);
    const maximumZoom = typeof map.getMaxZoom === "function" ? map.getMaxZoom() : 24;
    removeActivePopup();
    map.easeTo({
      center: feature.geometry.coordinates,
      zoom: Math.min(maximumZoom, Math.max(map.getZoom() + 1, expansionZoom)),
    });
  });
  map.on("click", config.pointLayerId, (event) => {
    const popup = showOverlayPopup(event, getLanguage());
    setActivePopup(popup, config.category);
  });
  [config.clusterLayerId, config.pointLayerId].forEach((layerId) => {
    map.on("mouseenter", layerId, () => { map.getCanvas().style.cursor = "pointer"; });
    map.on("mouseleave", layerId, () => { map.getCanvas().style.cursor = ""; });
  });
}

function showHexPopup(event, timeState, selectedSymptoms, language) {
  const feature = event.features?.[0];
  if (!feature || !window.mapboxgl) return null;
  return new window.mapboxgl.Popup({ closeButton: true, maxWidth: "250px" })
    .setLngLat(event.lngLat)
    .setHTML(humanHexPopupMarkup(feature.properties || {}, timeState, selectedSymptoms, language))
    .addTo(event.target);
}

export function humanHexPopupMarkup(properties = {}, timeState = null, selectedSymptoms = [], language = "EN") {
  const timeContext = timeState ? timeSummaryForHexReports(timeState, language) : formatCommunityReportCopy(language, "hex_all_dates");
  const symptomContext = popupSymptomContext(selectedSymptoms, language);
  return `
    <div class="hex-poc-popup">
      <strong>${formatCommunityReportCopy(language, "hex_popup_cell")}</strong>
      <span>${formatCommunityReportCopy(language, "hex_popup_total", { count: Number(properties.totalCount) })}</span>
      <span>${formatCommunityReportCopy(language, "hex_popup_time", { time: escapeHexPopupHtml(timeContext) })}</span>
      <span>${escapeHexPopupHtml(symptomContext)}</span>
      <span>${formatCommunityReportCopy(language, "hex_popup_resolution", { resolution: Number(properties.resolution) })}</span>
    </div>
  `;
}

function popupSymptomContext(selectedSymptoms, language) {
  const normalized = normalizeHexSymptomSelection(selectedSymptoms);
  if (normalized.length === 0) return formatCommunityReportCopy(language, "hex_popup_symptoms_all");
  if (normalized.length === 1) return formatCommunityReportCopy(language, "hex_popup_symptom", { symptom: hexSymptomLabel(normalized[0], language) });
  if (normalized.length === 2) return formatCommunityReportCopy(language, "hex_popup_symptoms", { symptoms: normalized.map((symptom) => hexSymptomLabel(symptom, language)).join(language === "ES" ? " y " : " and ") });
  return formatCommunityReportCopy(language, "hex_popup_filtered", { count: normalized.length });
}

function hexSymptomLabel(symptom, language) {
  if (language !== "ES") return symptom;
  if (symptom === "Cough/congestion") return formatCommunityReportCopy(language, "hex_symptom_cough_congestion");
  const keys = {
    Nausea: "pulse_symptom_nausea",
    "Difficulty breathing": "pulse_symptom_difficulty_breathing",
    "Sore throat": "pulse_symptom_sore_throat",
    Rash: "pulse_symptom_rash",
    Fever: "pulse_symptom_fever",
    Chills: "pulse_symptom_chills",
    Diarrhea: "pulse_symptom_diarrhea",
    "Bleeding from body openings": "pulse_symptom_bleeding",
    "Red eyes": "pulse_symptom_red_eyes",
    "Body aches": "pulse_symptom_body_aches",
    "Discolored or bloody urine": "pulse_symptom_discolored_or_bloody_urine",
    "Loss of smell or taste": "pulse_symptom_loss_of_smell_or_taste",
    "Yellow skin/eyes": "pulse_symptom_yellow_skin_or_eyes",
  };
  return formatCommunityReportCopy(language, keys[symptom]) || symptom;
}

function showOverlayPopup(event, language) {
  const feature = event.features?.[0];
  if (!feature || !window.mapboxgl) return null;
  return new window.mapboxgl.Popup({ closeButton: true, maxWidth: "250px" })
    .setLngLat(event.lngLat)
    .setHTML(overlayPopupMarkup(feature.properties || {}, language))
    .addTo(event.target);
}

function emptyFeatureCollection() {
  return { type: "FeatureCollection", features: [] };
}

function escapeHexPopupHtml(value) {
  return String(value ?? "").replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    "\"": "&quot;",
  })[character]);
}
